// URL-presets voor de CRM-werkruimte: een gewone hash die een zoektab opent met
// een kant-en-klaar filter. Beheer > User View schrijft ze (elk getal wordt een
// link), app/page.js leest ze en zet ze om in de bestaande candsearchPreset.
//
// Grammatica (vast, maar intern aan deze codec):
//   #zoeken-<tab>?owner=<id>&kw=<keyword>&c=<field>~<operator>~<value>
//   #processen?...                       (processen heeft geen zoeken- voorvoegsel)
// `c` mag herhaald worden; een datumbereik schrijft zijn waarde als <from>..<to>.
// Alles gaat door URLSearchParams, dus percent-encoding is afgehandeld; het
// scheidingsteken `~` wordt bij het lezen alleen op de EERSTE twee posities
// gesplitst, zodat een waarde zelf een `~` mag bevatten.
//
// Geen React, geen browser: puur string-werk, zodat de test hem los kan draaien.
//
// Weergavelabel (unit K, 4 okt 2026): `l=<field>~<label>` (herhaalbaar) geeft de
// conditie op dat veld een label voor de chip, zodat een ID-conditie
// ('contact.company_id is 16608197017890009181DiK') als 'Bedrijf is EDSN' leest. Het
// label is alleen weergave: lib/search/resolve.mjs leest field, operator en value,
// nooit label, dus het bereikt de SQL niet.
//
// Geen label voor een persoonsverwijzing (process.candidate_id, process.contact_id, unit K F7): zo komt
// er geen persoonsnaam in de URL; de chip krijgt de naam van de server (resource
// ref-names). De naamset van een procesverwijzing reist nooit in de URL (F2): de
// werkruimte haalt hem op bij dezelfde server-regel als het raster, een n= in een
// oude of aangepaste link wordt genegeerd.
//
// Functietitel (unit J, 4 okt 2026): `functie=<titel>` opent het CRM-raster met
// precies die functie. De hash draagt de titel zoals de gebruiker hem zag (alleen
// de witruimte rechtgezet, functieWhitespace), zodat chip, kruimel en tabbladtitel
// 'Java Developer' tonen. Vergeleken wordt op functieIdentity (lib/functie.mjs): de
// is-operator past hem toe op de waarde, dus 'Java  Developer' en 'java developer'
// geven dezelfde parameter en dezelfde rijen. Een functie-preset geldt
// voor Kandidaten, Contacten en Jobs tegelijk: elke tab krijgt zijn eigen
// identiteitsconditie (FUNCTIE_FIELD), zodat wisselen van tab het filter houdt.

import { functieIdentity, functieWhitespace } from '../functie.mjs';

// Tab-sleutel (het stuk in de hash) -> entiteit van de zoekcatalogus. De
// entiteitsnaam is tegelijk het voorvoegsel van zijn eigenaarsveld
// (candidate.owner, company.owner, contact.owner, job.owner, process.owner).
export const SEARCH_TABS = {
  kandidaten: 'candidate',
  klanten: 'company',
  prospects: 'company',
  contacten: 'contact',
  jobs: 'job',
  processen: 'process',
};

// Het titelveld per entiteit dat de functie-preset filtert, een bron voor de
// URL-codec, het raster (CandidateSearch) en de tests. Alle drie dragen
// identity:'functie' en functieIndex in de zoekcatalogus.
export const FUNCTIE_FIELD = Object.freeze({
  candidate: 'candidate.position',
  contact: 'contact.designation',
  job: 'job.name',
});

// Entiteit -> tab-sleutel in de hash, voor de drie functietabs.
export const FUNCTIE_TAB = Object.freeze({ candidate: 'kandidaten', contact: 'contacten', job: 'jobs' });

// De conditie van een functie-preset op een entiteit, of null. De waarde is de
// getoonde titel; de operator vergelijkt op identiteit.
export function functieCondition(entity, title) {
  const field = FUNCTIE_FIELD[entity];
  const shown = functieWhitespace(title);
  return field && functieIdentity(shown) ? { field, operator: 'is', value: shown } : null;
}

// Draagt deze conditielijst de functieconditie van de preset nog? Op identiteit
// vergeleken, dus een andere spelling van dezelfde titel telt mee. Haalt de
// gebruiker de chip weg (of verandert hij hem), dan is de functie-preset op die
// tab voorbij.
export function functieConditionHolds(conds, entity, functie) {
  const field = FUNCTIE_FIELD[entity];
  const key = functieIdentity(functie);
  if (!field || !key || !Array.isArray(conds)) return false;
  return conds.some((c) => c && c.field === field && c.operator === 'is' && functieIdentity(c.value) === key);
}

// Per functietab: staat de functieconditie er nog? Voor de tab-writer in
// app/page.js, die bij een tabwissel alleen functie= meeschrijft zolang het filter
// op de verlaten en de nieuwe tab nog staat.
export function functieHoldsByTab(all, functie) {
  return Object.fromEntries(Object.keys(FUNCTIE_FIELD).map((e) => [e, functieConditionHolds(all && all[e] && all[e].conds, e, functie)]));
}

// Zaait de werkruimtes van de drie functietabs in EEN keer uit een functie-preset
// (CandidateSearch, het preset-effect): elke tab krijgt alleen zijn eigen
// identiteitsconditie, een leeg trefwoord en zijn basisweergave. Andere tabs en
// andere instellingen van de tab (kolommen, sortering) blijven staan. `override`
// geeft de doeltab zijn eigen condities en trefwoord (de preset zelf, die naast de
// functie nog condities uit de URL kan dragen). Lege sleutel: `all` ongewijzigd.
export function seedFunctieWorkspaces(all, functie, { baseView = {}, decorate = (c) => c, override = {} } = {}) {
  if (!functieIdentity(functie)) return all;
  const next = { ...(all || {}) };
  for (const entity of Object.keys(FUNCTIE_FIELD)) {
    const own = override[entity];
    next[entity] = {
      ...((all && all[entity]) || {}),
      keyword: own && typeof own.keyword === 'string' ? own.keyword : '',
      conds: own && Array.isArray(own.conds) ? own.conds : [decorate(functieCondition(entity, functie))],
      view: baseView[entity] || 'all',
      savedId: null,
      savedFrom: null,
    };
  }
  return next;
}

// Het eigenaarsveld is in elke catalogus type 'text' (catalog.mjs:352 en
// catalogs-extra.mjs:389/457/533/731), dus de operator is `is`, niet `equals`.
const OWNER_OPERATOR = 'is';

function pad2(n) { return String(n).padStart(2, '0'); }

function dayBefore(day) {
  const [y, m, d] = String(day).split('-').map(Number);
  if (!y || !m || !d) return day;
  const t = new Date(Date.UTC(y, m - 1, d) - 86400000);
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

// UserViewTab levert een periode als { from, to } in 'YYYY-MM-DDTHH:mm' wandtijd,
// met een EXCLUSIEVE bovengrens (een maandpreset stuurt 00:00 van de dag erna).
// Een datumconditie in de zoekcatalogus is inclusief, dus de laatste dag is de
// dag ervoor zodra `to` op T00:00 staat.
export function rangeDays(from, to) {
  const f = String(from || '').slice(0, 10);
  const rawTo = String(to || '');
  const day = rawTo.slice(0, 10);
  const endsAtMidnight = rawTo.slice(10, 16) === 'T00:00';
  return { from: f, to: endsAtMidnight ? dayBefore(day) : day };
}

function encodeValue(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    if ('from' in value || 'to' in value) return `${value.from ?? ''}..${value.to ?? ''}`;
    return '';
  }
  return String(value);
}

function decodeValue(raw) {
  const s = String(raw ?? '');
  const i = s.indexOf('..');
  if (i < 0) return s;
  return { from: s.slice(0, i), to: s.slice(i + 2) };
}

// Velden waarvan het label nooit in de URL staat en nooit uit de URL komt.
export const NO_URL_LABEL = new Set(['process.candidate_id', 'process.contact_id']);

function hashBase(tab) {
  return tab === 'processen' ? '#processen' : `#zoeken-${tab}`;
}

export function buildSearchHash({ tab, owner = null, conds = [], keyword = '', functie = '' } = {}) {
  if (!SEARCH_TABS[tab]) return '';
  const qs = new URLSearchParams();
  const shown = functie ? functieWhitespace(functie) : '';
  if (functieIdentity(shown) && FUNCTIE_FIELD[SEARCH_TABS[tab]]) qs.set('functie', shown);
  if (owner != null && String(owner) !== '') qs.set('owner', String(owner));
  if (keyword) qs.set('kw', String(keyword));
  (Array.isArray(conds) ? conds : []).forEach((c) => {
    if (!c || !c.field) return;
    qs.append('c', `${c.field}~${c.operator || 'is'}~${encodeValue(c.value)}`);
  });
  (Array.isArray(conds) ? conds : []).forEach((c) => {
    const label = c && c.field && !NO_URL_LABEL.has(c.field) && typeof c.label === 'string' ? c.label.trim() : '';
    if (label) qs.append('l', `${c.field}~${label}`);
  });
  // e=<field>: deze conditie neemt rijen zonder waarde mee (includeEmpty, unit U5).
  // Een eigen sleutel naast c= en niet een vierde ~-deel, zodat een oude link en een
  // waarde met een ~ erin hetzelfde blijven lezen. Ontbreekt = strikt.
  (Array.isArray(conds) ? conds : []).forEach((c) => {
    if (c && c.field && c.includeEmpty === true) qs.append('e', c.field);
  });
  const q = qs.toString();
  return q ? `${hashBase(tab)}?${q}` : hashBase(tab);
}

// Leest de hash terug. Geeft null bij een onbekende tab of een hash zonder query,
// zodat de router die hash gewoon als normale navigatie laat passeren.
export function parseSearchHash(hash) {
  const raw = String(hash || '').replace(/^#/, '');
  const qi = raw.indexOf('?');
  if (qi < 0) return null;
  const query = raw.slice(qi + 1);
  if (!query) return null;
  const path = raw.slice(0, qi);
  const tab = path === 'processen' ? 'processen'
    : (path.startsWith('zoeken-') ? path.slice('zoeken-'.length) : null);
  const entity = tab ? SEARCH_TABS[tab] : null;
  if (!entity) return null;

  const qs = new URLSearchParams(query);
  const conds = [];
  const owner = qs.get('owner');
  if (owner) conds.push({ field: `${entity}.owner`, operator: OWNER_OPERATOR, value: String(owner) });
  qs.getAll('c').forEach((s) => {
    const str = String(s || '');
    const i = str.indexOf('~');
    if (i < 0) return;
    const j = str.indexOf('~', i + 1);
    if (j < 0) return;
    const field = str.slice(0, i);
    const operator = str.slice(i + 1, j);
    if (!field || !operator) return;
    conds.push({ field, operator, value: decodeValue(str.slice(j + 1)) });
  });

  // l=<field>~<label>: het label hoort bij de eerste conditie op dat veld die er nog
  // geen heeft.
  qs.getAll('l').forEach((s) => {
    const str = String(s || '');
    const i = str.indexOf('~');
    if (i <= 0) return;
    const field = str.slice(0, i);
    const label = str.slice(i + 1).trim();
    const target = label && !NO_URL_LABEL.has(field) && conds.find((c) => c.field === field && !c.label);
    if (target) target.label = label;
  });

  // e=<field>: de eerste conditie op dat veld zonder includeEmpty neemt lege waarden mee.
  qs.getAll('e').forEach((field) => {
    const target = field && conds.find((c) => c.field === field && c.includeEmpty !== true);
    if (target) target.includeEmpty = true;
  });

  // functie=<titel>: de getoonde titel blijft de waarde (witruimte rechtgezet), de
  // operator past de identiteit toe. Alleen op de drie functietabs.
  const fcond = functieCondition(entity, qs.get('functie') || '');
  if (fcond) conds.unshift(fcond);

  // De werkruimte kent Klanten en Prospects als twee tabs op EEN entiteit; de
  // preset-consument (CandidateSearch) kiest de prospect-tab op view:'prospects'.
  const preset = { entity, conds, keyword: qs.get('kw') || '' };
  if (fcond) preset.functie = fcond.value;
  if (tab === 'prospects') preset.view = 'prospects';
  return { tab, entity, preset };
}

// ── De URL volgt de bewerking (unit K, sluiting 4 okt 2026) ─────────────────────
// Na een URL-preset hoort de hash te tonen wat de gebruiker ziet. Haalt hij een chip
// weg, past hij er een aan, zet hij er een bij of wist hij alles, dan schrijft
// app/page.js de hash van de nieuwe stand (replace, geen nieuwe entry), zodat herladen,
// Terug of het sluiten van een venster het weggehaalde filter niet terugbrengt.

// Overleeft deze conditie de URL heel? Alleen als de waarde na schrijven en lezen
// dezelfde is (tekst of een datumbereik van tekst); een lijst of een afstandsanker niet.
function condRoundTrips(c) {
  if (!c || typeof c.field !== 'string' || !c.field || typeof c.operator !== 'string' || !c.operator) return false;
  if (c.field.includes('~') || c.operator.includes('~')) return false;
  if (c.value == null) return true;
  return JSON.stringify(decodeValue(encodeValue(c.value))) === JSON.stringify(c.value);
}

/** Wat telt als 'dezelfde stand': veld, operator, waarde en het zoekwoord. */
export function presetEditSignature(conds, keyword = '') {
  const list = (Array.isArray(conds) ? conds : []).filter((c) => c && c.field)
    .map((c) => (c.includeEmpty === true ? [c.field, c.operator || 'is', c.value == null ? '' : c.value, 1] : [c.field, c.operator || 'is', c.value == null ? '' : c.value]));
  return JSON.stringify([list, String(keyword || '').trim()]);
}

/**
 * De hash na een bewerking van de preset-tab. `tab` is de URL-tab (kandidaten,
 * processen, ...). Een label (l=) alleen voor een conditie die er met precies die
 * waarde nog staat (`labels` uit de preset). Kan een conditie niet in de URL, of is er
 * niets meer, dan de kale tab-hash.
 */
export function presetHashAfterEdit({ tab, conds = [], keyword = '', labels = [] } = {}) {
  if (!SEARCH_TABS[tab]) return '';
  const list = (Array.isArray(conds) ? conds : []).filter((c) => c && c.field);
  if (!list.every(condRoundTrips)) return hashBase(tab);
  const withLabels = list.map(({ field, operator, value, includeEmpty }) => {
    const hit = (Array.isArray(labels) ? labels : []).find((l) => l && l.field === field && JSON.stringify(l.value) === JSON.stringify(value) && l.label);
    const base = includeEmpty === true ? { field, operator, value, includeEmpty: true } : { field, operator, value };
    return hit ? { ...base, label: hit.label } : base;
  });
  return buildSearchHash({ tab, conds: withLabels, keyword: String(keyword || '').trim() });
}

/** De labels van een geparste preset, voor presetHashAfterEdit. */
export function presetLabels(conds) {
  return (Array.isArray(conds) ? conds : []).filter((c) => c && c.field && typeof c.label === 'string' && c.label)
    .map((c) => ({ field: c.field, value: c.value, label: c.label }));
}
